/**
 * The matcher's tuning, fetched rather than compiled.
 *
 * Three fixes in one day - subset set names, suffixed numbers outranking the
 * set, demoting Jumbo printings - each needed an app release to reach a phone,
 * while the repair loop that diagnosed all three sat filing them as tier 3
 * "needs_code_change". None of them were logic. They were a word list, a flag
 * and a penalty, and those belong in a row.
 *
 * DEFAULTS are the same values the server is seeded with. They are not a
 * placeholder: if the fetch is slow, offline, or the table is empty, matching
 * runs on them and nobody loses a price waiting for config.
 */

import { supabase } from '../lib/supabase';

export const DEFAULTS = Object.freeze({
  setFiller: [
    'base', 'set', 'sets', 'the', 'and', 'promos', 'promo', 'cards', 'card',
    'series', 'collection', 'expansion', 'sm', 'swsh', 'sv', 'xy', 'me', 'hgss',
    'bw', 'dp', 'pl', 'ex', 'gym', 'neo', 'col', 'tcgp', 'pop',
  ],
  eraCodes: ['sm', 'swsh', 'sv', 'xy', 'me', 'hgss', 'bw', 'dp', 'pl', 'ex'],
  suffixedNumberOverridesSet: true,
  // A stamped printing the vendor files away from its parent set: Team Rocket 8
  // prerelease Dark Gyarados sits in "WoTC Promo". Number plus stamp identifies
  // it, so the set name stops being evidence once both agree.
  stampOverridesSet: true,
  demote: [{ pattern: '\\bjumbo\\b', penalty: 25, why: 'oversized promo, not the card' }],
  singlePrintingBeatsFinish: true,
});

let current = DEFAULTS;
let inFlight = null;
let loadedAt = 0;

// Long enough that a phone is not re-asking all day, short enough that a fix
// lands the same session someone reopens the app.
const TTL_MS = 30 * 60 * 1000;

/** Whatever we have right now. Never null, never waits. */
export function matchConfig() {
  return current;
}

/**
 * Refresh from the server, at most once per TTL. Safe to call on every lookup.
 *
 * Failure is silent by design. A matcher that stopped working because a config
 * table was unreachable would be worse than one running slightly old rules.
 */
export function loadMatchConfig({ force = false } = {}) {
  if (!force && Date.now() - loadedAt < TTL_MS) return Promise.resolve(current);
  if (inFlight) return inFlight;

  inFlight = supabase
    .from('pricing_match_config')
    .select('config, version')
    .eq('vendor', 'poketrace')
    .eq('active', true)
    .maybeSingle()
    .then(({ data, error }) => {
      if (!error && data?.config) {
        // Merged over the defaults rather than replacing them, so a config that
        // omits a key falls back instead of switching that rule off entirely.
        current = { ...DEFAULTS, ...data.config };
        loadedAt = Date.now();
      }
      return current;
    })
    .catch(() => current)
    .finally(() => { inFlight = null; });

  return inFlight;
}

/** Compiled once per config, not once per candidate. */
let demoteCache = { source: null, rules: [] };

export function demoteRules() {
  const raw = current.demote || [];
  if (demoteCache.source === raw) return demoteCache.rules;
  const rules = [];
  for (const r of raw) {
    try {
      rules.push({ re: new RegExp(r.pattern, 'i'), penalty: Number(r.penalty) || 0 });
    } catch {
      // A bad pattern from the server must not take the matcher down with it.
      console.warn('[MatchConfig] ignoring unparseable demote pattern:', r?.pattern);
    }
  }
  demoteCache = { source: raw, rules };
  return rules;
}

export default { matchConfig, loadMatchConfig, demoteRules, DEFAULTS };
